import { useEffect, useRef, useState } from "react";
import { createAccount, deleteAccount, updateAccount } from "../../api";
import { usePositions } from "../../hooks";
import { useAccount } from "../../hooks/useAccount";
import { useTransactions } from "../../hooks/useTransactions";
import { deleteAllTrades } from "../../api/Trades";
import { deleteAllCashFlows } from "../../api/Cashflow";
import { Account } from "../../types/Account";
import { useDebounce } from "../../features/hooks";
import { track, EVENTS } from "../../utils/analytics";

interface AccountController {
  accountForm: any;
  editingId: string | null;
  handleFormChange: (e: any) => void;
  handleSubmit: (e: any) => void;
  handleEdit: (account: any) => void;
  handleDelete: (accountId: number) => void;
  setSelectedAccount: (account: any) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  deleteAllTheTrades: (accountId: number) => void;
  updateAccountName: (
    accountId: number,
    name: string,
    account: Account
  ) => void;
}

const useAccountController = () => {
  const [accountForm, setAccountForm] = useState({ name: "", userId: "" });
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const initialised = useRef<boolean>(false);

  const positions = usePositions();
  const accounts = useAccount();
  const transactions = useTransactions();

  useEffect(() => {
    if (initialised.current === false) {
      initialised.current = true;
      accounts.retrieveAccounts(false);
    }
  }, []);

  const deleteAllTheTrades = async (accountId: number) => {
    // Track the action of deleting all trades
    track(EVENTS.DATA_MANAGEMENT.DATA_BACKED_UP, {
      action: "delete_all_trades",
      account_id: accountId,
    });

    await deleteAllTrades(accountId);
    await deleteAllCashFlows(accountId);

    transactions.getTradesAndCashflows(accountId);
    positions.fetchPositions(accountId);
  };

  const setSelectedAccount = async (account: any) => {
    // Track account switching
    track(EVENTS.NAVIGATION.PAGE_NAVIGATED, {
      action: "account_switched",
      account_id: account.id,
      account_name: account.name,
    });

    accounts.setSelectedAccount(account);
    transactions.getTradesAndCashflows(account.id);
    positions.fetchPositions(account.id);
  };

  const handleFormChange = (e) => {
    setAccountForm({ ...accountForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      await updateAccount(editingId, accountForm);

      // Track account update
      track(EVENTS.SETTINGS.PROFILE_UPDATED, {
        action: "account_updated",
        account_id: editingId,
        account_name: accountForm.name,
      });
    } else {
      const newAccount = await createAccount(accountForm);

      // Track new account creation
      track(EVENTS.DATA_MANAGEMENT.DATA_BACKED_UP, {
        action: "account_created",
        account_name: accountForm.name,
      });
    }
    setAccountForm({ name: "", userId: "" });
    setEditingId(null);
    accounts.retrieveAccounts(true);
  };

  const handleEdit = (account) => {
    setAccountForm({ name: account.name, userId: account.userId });
    setEditingId(account.id);
  };

  const handleDelete = async (accountId: number) => {
    // Track account deletion
    track(EVENTS.DATA_MANAGEMENT.DATA_BACKED_UP, {
      action: "account_deleted",
      account_id: accountId,
    });

    const result = await deleteAccount(accountId);
    window.location.reload();
  };

  const updateAccountName = useDebounce(
    async (accountId: number, name: string, account: Account) => {
      await updateAccount(accountId, {
        ...account,
        name: name,
      });

      // Track account name update
      track(EVENTS.SETTINGS.PROFILE_UPDATED, {
        action: "account_name_updated",
        account_id: accountId,
        new_name: name,
      });

      accounts.retrieveAccounts(true);
    },
    200
  );

  return {
    accountForm,
    editingId,
    handleFormChange,
    handleSubmit,
    handleEdit,
    handleDelete,
    setSelectedAccount,
    modalOpen,
    setModalOpen,
    deleteAllTheTrades,
    updateAccountName,
  } as AccountController;
};

export default useAccountController;
